import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';

/** MUI */
import Box from '@mui/system/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';

/** HOOKS */
import useAuth from 'hooks/useAuth';
import useFilters from 'hooks/useFilters';
import { useCookieState } from 'hooks/useCookieState';

// Utils & Const
import { noOp } from 'utils/functions';

/** CMPS */
import CustomTabPanel from './CustomTabPanel';

const styles = {
  container: {
    width: '100%',
  },

  tabButtonsContainer: {
    display: 'flex',
    justifyContent: 'center',
  },
};

function a11yProps(index, label) {
  return {
    id: `${label}-tab-${index}`,
    'aria-controls': `${label}-tabpanel-${index}`,
  };
}

const customTabsDefaultPropSx = {};

const customTabsDefaultPropConfig = {
  label: 'button',
  defaultTabIndex: {
    authenticated: 0,
    nonAuthenticated: 0,
  },
};

const CustomTabs = ({
  tabs,
  sx = customTabsDefaultPropSx,
  config = customTabsDefaultPropConfig,
  onTabChange = noOp,
}) => {
  const router = useRouter();
  const { query } = router;
  const { layoutRef } = useFilters();

  const { isAuthenticated } = useAuth();
  const { label, defaultTabIndex } = config;

  const [setInitialTabCookie] = useCookieState('cookieTab');

  const [tabPanel, setTabPanel] = useState(() => {
    if (isAuthenticated) {
      if (query?.tab) {
        const currentQueryTab = tabs.findIndex(({ tabName }) => tabName.toLowerCase() === query.tab.toLowerCase());
        return currentQueryTab;
      }
      return defaultTabIndex.authenticated;
    }
    return defaultTabIndex.nonAuthenticated;
  });

  const handleChange = (_, newValue) => {
    setTabPanel(newValue);

    const newQuery = {
      ...router.query,
      tab: tabs[newValue]?.tabName,
      ...(tabs[newValue]?.tabLimit && { limit: tabs[newValue].tabLimit }),
    };

    delete newQuery['item-id'];

    router.push(
      {
        ...router,
        query: newQuery,
      },
      undefined,
      { shallow: true }
    );
    setInitialTabCookie(tabs[newValue]?.tabName);
    onTabChange(tabs[newValue]);
  };

  useEffect(() => {
    if (query?.tab) {
      const currentQueryTab = tabs.findIndex(({ tabName }) => tabName.toLowerCase() === query.tab.toLowerCase());
      setTabPanel(currentQueryTab);
    } else {
      setTabPanel(isAuthenticated ? defaultTabIndex.authenticated : defaultTabIndex.nonAuthenticated);
    }
  }, [isAuthenticated, query?.tab, defaultTabIndex.nonAuthenticated, defaultTabIndex.authenticated, tabs]);

  return (
    <Box ref={layoutRef} sx={{ ...styles.container, ...sx }}>
      <Box sx={styles.tabButtonsContainer}>
        <Tabs value={tabPanel} onChange={handleChange} aria-label={`${label} tabs`}>
          {tabs.map(({ tabName, authProtected = false }, i) => {
            if (authProtected) {
              if (isAuthenticated) {
                return (
                  <Tab
                    key={tabName + String(i)}
                    label={tabName}
                    {...a11yProps(0, label)}
                    data-testid={`${label}-tabs__tab--${tabName}`}
                  />
                );
              }

              return null;
            }
            return (
              <Tab
                key={tabName + String(i)}
                label={tabName}
                {...a11yProps(0, label)}
                data-testid={`${label}-tabs__tab--${tabName}`}
              />
            );
          })}
        </Tabs>
      </Box>
      {tabs.map(({ tabName, tabContent, authProtected = false }, i) => {
        if (authProtected) {
          if (isAuthenticated) {
            return (
              <CustomTabPanel value={tabPanel} index={i} key={`${tabName}-${String(i)}`}>
                {tabContent}
              </CustomTabPanel>
            );
          }

          return null;
        }
        return (
          <CustomTabPanel value={tabPanel} index={i} key={`${tabName}-${String(i)}`}>
            {tabContent}
          </CustomTabPanel>
        );
      })}
    </Box>
  );
};

export default CustomTabs;

CustomTabs.propTypes = {
  sx: PropTypes.shape({}),
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      tabName: PropTypes.string.isRequired,
      tabLimit: PropTypes.number,
      tabContent: PropTypes.oneOfType([PropTypes.elementType, PropTypes.func, PropTypes.shape({})]).isRequired,
    })
  ).isRequired,
  config: PropTypes.shape({
    label: PropTypes.string,
    defaultTabIndex: PropTypes.shape({
      authenticated: PropTypes.number,
      nonAuthenticated: PropTypes.number,
    }),
  }),
  onTabChange: PropTypes.func,
};
